// พิมพ์กระดาษคำตอบของทั้งห้อง — หนึ่งหน้าต่อนักเรียนหนึ่งคน ชื่อและรหัสแผ่นพิมพ์มาให้แล้ว
//
// ต้องพิมพ์ขนาดจริง (สเกล 100%) — ตัวอ่านกระดาษจัดภาพจากมุมสี่เหลี่ยม จึงทนการย่อได้บ้าง
// แต่ "ย่อให้พอดีหน้า" ของบางเครื่องตัดมุมทิ้ง แล้วแผ่นนั้นจะอ่านไม่ได้เลย
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Form } from 'react-bootstrap'
import { Link, useParams } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'
import axiosInstance from '../services/axiosInterceptor'
import useSchoolSettings from '../hooks/useSchoolSettings'
import { API, roomLabel, showError } from './examUtils'
import AnswerSheet from './AnswerSheet'
import { bubbleLayout } from './sheetLayout'
import { encodeSheetCode } from './sheetCode'
import '../print.css'

// กระดาษสำรองต่อท้ายชุดของห้อง — กันแผ่นจริงหาย/เปื้อน/นักเรียนย้ายเข้าหลังพิมพ์
// ไม่ผูกกับใคร นักเรียนเขียนชื่อและฝนเลขประจำตัวเอง ครูยืนยันทุกแผ่นตอนสแกน (ดู AnswerSheet / reader)
const MAX_SPARE = 50

const PAGE_STYLE = `
  @page { size: A4 portrait; margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff; }
  /* สูงน้อยกว่า A4 นิดหนึ่ง — สูงเท่ากันพอดี เศษของการปัดทำให้ได้หน้าขาวคั่นทุกแผ่น (ขอบล่างว่างอยู่แล้ว) */
  .exam-page { margin: 0 !important; box-shadow: none !important; height: 296mm; overflow: hidden;
               page-break-after: always; break-after: page; }
  .exam-page:last-child { page-break-after: auto; break-after: auto; }
  .exam-sheet { display: block; }
`

const inactive = (s) => String(s.st_status) === '0' || s.transfer_room_id != null

export default function ExamPrint() {
  const { examId, teachId } = useParams()
  const school = useSchoolSettings()
  const [data, setData] = useState(null)
  const [roster, setRoster] = useState([])
  const [onlyMissing, setOnlyMissing] = useState(false)
  const [spareCount, setSpareCount] = useState(2)
  const contentRef = useRef(null)

  useEffect(() => {
    Promise.all([
      axiosInstance.get(`${API}/exams/${examId}`),
      axiosInstance.get(`${API}/exams/${examId}/rooms/${teachId}`),
    ])
      .then(([exam, rows]) => {
        setData(exam.data)
        setRoster(rows.data)
      })
      .catch((err) => showError(err, 'โหลดข้อมูลไม่สำเร็จ'))
  }, [examId, teachId])

  const room = data?.rooms.find((r) => r.teach_id === Number(teachId))
  const students = useMemo(
    () => roster.filter((s) => !inactive(s) && (!onlyMissing || !s.answers)),
    [roster, onlyMissing]
  )

  const print = useReactToPrint({
    contentRef,
    documentTitle: data && room ? `กระดาษคำตอบ ${data.exam.exam_name} ${roomLabel(room)}` : 'กระดาษคำตอบ',
    pageStyle: PAGE_STYLE,
  })

  if (!data || !room) return <div className="p-4">กำลังโหลด…</div>

  let sheetError = null
  try {
    // ตรวจก่อนวาด — เกินหนึ่งแผ่นหรือเลขเกินแถบรหัส ให้บอกครู ไม่ใช่หน้าขาวทั้งหน้า
    bubbleLayout(data.questions, data.exam.exam_choices)
    for (const s of students) encodeSheetCode(data.exam.exam_id, s.study_id)
  } catch (err) {
    sheetError = err.message
  }
  // กระดาษสำรองรับข้อปรนัยได้น้อยกว่า (คอลัมน์สุดท้ายเป็นช่องฝนเลขประจำตัว) — เกินแล้วพิมพ์แผ่นจริงได้ตามปกติ
  let spareError = null
  try {
    bubbleLayout(data.questions, data.exam.exam_choices, true)
  } catch (err) {
    spareError = err.message
  }
  const spares = sheetError || spareError ? 0 : spareCount

  return (
    <div className="p-3" style={{ background: '#e9ecef', minHeight: '100vh' }}>
      <div className="d-print-none mx-auto mb-3" style={{ maxWidth: '210mm' }}>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <Link to={`/teaching/exams/${examId}`} className="btn btn-light btn-sm">
            <i className="ri-arrow-left-line"></i> กลับ
          </Link>
          <div className="me-auto fw-semibold">
            {data.exam.exam_name} · ห้อง {roomLabel(room)} · {students.length} แผ่น{spares ? ` + สำรอง ${spares}` : ''}
          </div>
          <Form.Check type="switch" id="only-missing" label="เฉพาะคนที่ยังไม่มีคำตอบ"
            checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} />
          <Form.Group className="d-flex align-items-center gap-1" controlId="spare-count">
            <Form.Label className="mb-0 fs-sm text-nowrap">กระดาษสำรอง</Form.Label>
            <Form.Control type="number" size="sm" min={0} max={MAX_SPARE} style={{ width: 64 }}
              value={spareCount} disabled={!!spareError}
              onChange={(e) => setSpareCount(Math.max(0, Math.min(MAX_SPARE, Math.trunc(Number(e.target.value)) || 0)))} />
            <span className="fs-sm">แผ่น</span>
          </Form.Group>
          <Button size="sm" disabled={(!students.length && !spares) || !!sheetError} onClick={print}>
            <i className="ri-printer-line"></i> พิมพ์
          </Button>
        </div>
        {sheetError && <Alert variant="danger" className="py-2 fs-sm">{sheetError}</Alert>}
        {!sheetError && spareError && (
          <Alert variant="warning" className="py-2 fs-sm">{spareError} — ข้อสอบนี้พิมพ์กระดาษสำรองไม่ได้</Alert>
        )}
        <Alert variant="info" className="py-2 fs-sm mb-0">
          ตั้งค่าเครื่องพิมพ์เป็นกระดาษ A4 ขนาดจริง (สเกล 100%) ไม่ใช่ "ย่อให้พอดีหน้า" —
          สี่เหลี่ยมดำสี่มุมต้องอยู่ครบทุกแผ่น ระบบใช้จัดภาพตอนสแกน · กระดาษแต่ละแผ่นผูกกับนักเรียนคนนั้น ห้ามสลับกัน ·
          กระดาษสำรองใช้แทนแผ่นที่หาย/เปื้อน นักเรียนเขียนชื่อและฝนเลขประจำตัวเอง ตอนสแกนครูต้องยืนยันชื่อทุกแผ่น
        </Alert>
      </div>

      {!sheetError && (
        <div ref={contentRef}>
          {students.map((s) => (
            <div key={s.study_id} className="exam-page mx-auto mb-3 shadow-sm" style={{ width: '210mm', background: '#fff' }}>
              <AnswerSheet exam={data.exam} questions={data.questions} student={s} room={room} school={school} />
            </div>
          ))}
          {Array.from({ length: spares }, (_, i) => (
            <div key={`spare-${i}`} className="exam-page mx-auto mb-3 shadow-sm" style={{ width: '210mm', background: '#fff' }}>
              <AnswerSheet exam={data.exam} questions={data.questions} room={room} school={school} spare />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
